import { useEffect, useState, useCallback } from "react";
import httpService from "../../services/httpService";
import eventBus from "../../services/eventBus";

import Form from 'react-bootstrap/Form';

import UserRecordsTable from "./UserRecordsTable";
import UserEditModal from "./UserEditModal";
import DeleteRecordModal from "./DeleteRecordModal";

const UserRecordsTab = ({ endpoint, label }) => {
    const [records, setRecords]       = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [page, setPage]             = useState(1);
    const [search, setSearch]                     = useState('');
    const [debouncedSearch, setDebouncedSearch]   = useState('');

    const [selected, setSelected]   = useState(null);
    const [form, setForm]           = useState({});
    const [saving, setSaving]       = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting]         = useState(false);

    const [testContent, setTestContent]   = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [sendingNotif, setSendingNotif] = useState(false);

    const [activeTab, setActiveTab]         = useState('activity');
    const [listenerClubs, setListenerClubs] = useState([]);
    const [clubsLoading, setClubsLoading]   = useState(false);

    const [impersonating, setImpersonating] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback((p) => {
        setLoading(true);
        const qs = new URLSearchParams({ page: p });
        if (debouncedSearch) qs.set('search', debouncedSearch);
        httpService.get(`${endpoint}?${qs}`)
            .then(res => {
                setRecords(res.data.data ?? []);
                setPagination(res.data.pagination ?? null);
            })
            .catch(err => setError(err.response?.data?.message || `Failed to load ${label}s`))
            .finally(() => setLoading(false));
    }, [endpoint, label, debouncedSearch]);

    useEffect(() => { load(page); }, [page, load]);

    const openEdit = (record) => {
        setSelected(record);
        setActiveTab('activity');
        setForm({
            first_name:        record.user.first_name,
            last_name:         record.user.last_name,
            email:             record.user.email,
            activated:         record.user.activated,
            subscription_type: record.subscription_type ?? '',
            contact_info:      record.contact_info ?? '',
        });
        if (label === 'Listener') {
            setClubsLoading(true);
            httpService.get(`/admin/listeners/${record.guid}/clubs`)
                .then(res => setListenerClubs(res.data.data ?? []))
                .catch(() => setListenerClubs([]))
                .finally(() => setClubsLoading(false));
        }
    };

    const closeEdit = () => { setSelected(null); setForm({}); setTestContent(''); setListenerClubs([]); setActiveTab('activity'); };

    const handleSave = () => {
        setSaving(true);
        httpService.put(`${endpoint}/${selected.guid}`, form)
            .then(res => {
                const updated = res.data.data;
                setRecords(rs => rs.map(r => r.guid === updated.guid ? updated : r));
                closeEdit();
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    const handleImpersonate = () => {
        setImpersonating(true);
        httpService.post(`/admin/podcasters/${selected.guid}/impersonate`)
            .then(res => {
                const url = `${window.location.origin}/impersonate?token=${res.data.token}`;
                navigator.clipboard.writeText(url)
                    .then(() => eventBus.emit('toast', { type: 'success', message: 'Magic link copied — paste it in an incognito window' }))
                    .catch(() => eventBus.emit('toast', { type: 'warning', message: 'Copy failed — link: ' + url }));
            })
            .catch(() => {})
            .finally(() => setImpersonating(false));
    };

    const handleSendTestEmail = () => {
        setSendingEmail(true);
        httpService.post('/admin/test/send-email', { listener_guid: selected.guid, content: testContent })
            .catch(() => {}).finally(() => setSendingEmail(false));
    };

    const handleSendTestNotification = () => {
        setSendingNotif(true);
        httpService.post('/admin/test/send-notification', { listener_guid: selected.guid, content: testContent })
            .catch(() => {}).finally(() => setSendingNotif(false));
    };

    const handleDelete = () => {
        setDeleting(true);
        httpService.delete(`${endpoint}/${deleteTarget.guid}`)
            .then(() => {
                setRecords(rs => rs.filter(r => r.guid !== deleteTarget.guid));
                setDeleteTarget(null);
            })
            .catch(() => {})
            .finally(() => setDeleting(false));
    };

    return (
        <div className={'pb-5'}>
            <div className="mb-3">
                <Form.Control
                    type="search"
                    placeholder={`Search ${label}s by name or email…`}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <UserRecordsTable
                records={records}
                label={label}
                loading={loading}
                error={error}
                debouncedSearch={debouncedSearch}
                pagination={pagination}
                onPageChange={setPage}
                onEdit={openEdit}
                onDeleteRequest={setDeleteTarget}
            />

            <UserEditModal
                selected={selected}
                label={label}
                form={form}
                onChangeForm={setForm}
                onSave={handleSave}
                onClose={closeEdit}
                saving={saving}
                onImpersonate={handleImpersonate}
                impersonating={impersonating}
                activeTab={activeTab}
                onChangeActiveTab={setActiveTab}
                listenerClubs={listenerClubs}
                clubsLoading={clubsLoading}
                testContent={testContent}
                onChangeTestContent={setTestContent}
                onSendTestEmail={handleSendTestEmail}
                onSendTestNotification={handleSendTestNotification}
                sendingEmail={sendingEmail}
                sendingNotif={sendingNotif}
            />

            <DeleteRecordModal
                target={deleteTarget}
                label={label}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
                deleting={deleting}
            />
        </div>
    );
};

export default UserRecordsTab;
